import {
  Alignment,
  BottomNavigationDrawer,
  DEFAULT_FONT_FAMILY,
  Dialog,
  Displaying,
  Spacing,
  Tealx,
  Typography,
} from "@bonusx-global/bx-design-system";
import { HomeCareSolid, LeafEcoSolid, RocketSolid, TimesSolid } from "@bonusx-global/bx-icons";
import { styled } from "@mui/material";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import _ from "lodash";
import { memo, useCallback } from "react";

const LevelItemContainer = styled(Box)({
  display: Displaying.flex,
  alignItems: Alignment.start,
  gap: Spacing["750"],
  marginBottom: Spacing["1000"],
});

const IconContainer = styled(Box)({
  display: Displaying.flex,
  alignItems: Alignment.center,
  justifyContent: Alignment.center,
  minWidth: Spacing["1500"],
  minHeight: Spacing["1500"],
  color: Tealx["400"],
  paddingTop: Spacing["250"],
});

const TextContainer = styled(Box)({
  display: Displaying.flex,
  flexDirection: "column",
  gap: Spacing["250"],
  width: "100%",
});

const CompletionLevelBadge = styled(Box)({
  display: Displaying.inlineFlex,
  padding: `${Spacing["250"]} ${Spacing["750"]}`,
  gap: Spacing["250"],
  borderRadius: Spacing["1500"],
  backgroundColor: "transparent",
  border: `1px solid ${Tealx["400"]}`,
  color: Tealx["400"],
  fontSize: "0.75rem",
  fontFamily: DEFAULT_FONT_FAMILY,
  fontWeight: 700,
});

const DrawerHeader = styled(Box)({
  display: Displaying.flex,
  justifyContent: Alignment.spaceBetween,
  alignItems: Alignment.center,
  marginBottom: Spacing["1500"],
});

const DrawerContent = styled(Box)({
  padding: Spacing["1000"],
  paddingTop: Spacing["0"],
});

interface ProfileCompletionLevelExplanationProps {
  open: boolean;
  onClose: () => void;
  isMobile: boolean;
  activeStep?: number;
}

const EXPLANATION_TITLE = "Completezza del profilo";
const YOUR_LEVEL_BADGE_TEXT = "Il tuo livello";

const LEVEL_PRIMI_PASSI_TITLE = "Primi passi";
const LEVEL_PRIMI_PASSI_DESCRIPTION = "Hai compilato i tuoi dati personali del tutto o in parte.";

const LEVEL_EFFICACE_TITLE = "Efficace";
const LEVEL_EFFICACE_DESCRIPTION = "Hai completato i tuoi dati personali, le attività e il nucleo familiare.";

const LEVEL_STELLARE_TITLE = "Stellare!";
const LEVEL_STELLARE_DESCRIPTION =
  "Hai inserito tutte le informazioni! Ricorda puoi aggiornarle o modificarle in ogni momento.";

function LevelItem({
  icon,
  title,
  description,
  isActive,
}: {
  readonly icon: JSX.Element;
  readonly title: string;
  readonly description: string;
  readonly isActive?: boolean;
}): JSX.Element {
  return (
    <LevelItemContainer>
      <IconContainer>{icon}</IconContainer>
      <TextContainer>
        <Box
          sx={{
            display: Displaying.flex,
            justifyContent: Alignment.spaceBetween,
            alignItems: Alignment.center,
            width: "100%",
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          {isActive && <CompletionLevelBadge>{YOUR_LEVEL_BADGE_TEXT}</CompletionLevelBadge>}
        </Box>
        <Box sx={{ paddingTop: Spacing["250"] }}>
          <Typography variant="body2">{description}</Typography>
        </Box>
      </TextContainer>
    </LevelItemContainer>
  );
}

function ProfileCompletionLevelExplanationContent({ activeStep }: { readonly activeStep?: number }): JSX.Element {
  return (
    <Box>
      <LevelItem
        icon={<LeafEcoSolid sx={{ fontSize: Spacing["1500"] }} />}
        title={LEVEL_PRIMI_PASSI_TITLE}
        description={LEVEL_PRIMI_PASSI_DESCRIPTION}
        isActive={activeStep === 0}
      />
      <LevelItem
        icon={<HomeCareSolid sx={{ fontSize: Spacing["1500"] }} />}
        title={LEVEL_EFFICACE_TITLE}
        description={LEVEL_EFFICACE_DESCRIPTION}
        isActive={activeStep === 1}
      />
      <LevelItem
        icon={<RocketSolid sx={{ fontSize: Spacing["1500"] }} />}
        title={LEVEL_STELLARE_TITLE}
        description={LEVEL_STELLARE_DESCRIPTION}
        isActive={activeStep === 2}
      />
    </Box>
  );
}

function ProfileCompletionLevelExplanationGenerator({
  open,
  onClose,
  isMobile,
  activeStep,
}: Readonly<ProfileCompletionLevelExplanationProps>): JSX.Element {
  const handleClose = useCallback(
    function handleClose() {
      onClose();
    },
    [onClose]
  );

  const handleDrawerOpen = useCallback(function handleDrawerOpen() {
    _.noop();
  }, []);

  if (isMobile) {
    return (
      <BottomNavigationDrawer open={open} onClose={handleClose} onOpen={handleDrawerOpen}>
        <DrawerContent>
          <DrawerHeader>
            <Typography variant="subtitle1" sx={{ fontSize: Spacing["1500"] }}>
              {EXPLANATION_TITLE}
            </Typography>
            <IconButton onClick={handleClose}>
              <TimesSolid />
            </IconButton>
          </DrawerHeader>
          <ProfileCompletionLevelExplanationContent activeStep={activeStep} />
        </DrawerContent>
      </BottomNavigationDrawer>
    );
  }

  return (
    <Dialog
      id="profile-completion-level-explanation-dialog"
      open={open}
      onClose={handleClose}
      onCloseButtonClick={handleClose}
      title={EXPLANATION_TITLE}
    >
      <Box sx={{ paddingTop: Spacing["1500"] }}>
        <ProfileCompletionLevelExplanationContent activeStep={activeStep} />
      </Box>
    </Dialog>
  );
}

export const ProfileCompletionLevelExplanation = memo(ProfileCompletionLevelExplanationGenerator);
